import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '@/utils/api';
import axios from 'axios';
import {
  Target, Sparkles, MessageCircle, TrendingUp,
  Users, Zap, Send, PawPrint, Image, Lightbulb, CheckCircle2
} from 'lucide-react';

/* ═══ COPY PER SEGMENT ═══ */
const COPY = {
  hodowla: {
    '/panel': {
      icon: Target, iconBg: 'bg-[#F47A3B]/10 text-[#F47A3B]',
      title: 'Twój panel',
      text: 'Tu widzisz ile osób widziało Twoje posty i ile napisało w sprawie szczeniąt.',
      tip: 'Kliknij „Posty na FB" → zaczniesz zbierać zapytania o mioty',
    },
    '/panel/kampania': {
      icon: Sparkles, iconBg: 'bg-[#F47A3B]/10 text-[#F47A3B]',
      title: 'Twoje reklamy',
      text: 'To są gotowe reklamy Twojej hodowli. Kliknij „Publikuj" — zaczniesz zbierać zapytania.',
      tip: 'Publikuj 1–2 dziennie → więcej klientów na szczenięta',
    },
    '/panel/kreator-reklam': {
      icon: Image, iconBg: 'bg-purple-50 text-purple-600',
      title: 'Kreator reklam',
      text: 'W 10 sekund masz reklamę hodowli gotową do publikacji.',
      tip: 'Wrzuć zdjęcie psa → stworzymy reklamę',
    },
    '/panel/publikacja': {
      icon: Send, iconBg: 'bg-emerald-50 text-emerald-600',
      title: 'Publikacja',
      text: 'Masz gotowy post do publikacji. Jeden klik i jest na Facebooku.',
      tip: 'Opublikuj teraz → zapytania zaczynają napływać',
    },
    '/panel/profil': {
      icon: Users, iconBg: 'bg-blue-50 text-blue-600',
      title: 'Profil hodowli',
      text: 'Lepszy profil = więcej trafnych zapytań o szczenięta. Uzupełnij dane, zdjęcia i rasy.',
      tip: 'Uzupełnij wszystkie zakładki — klienci chcą widzieć hodowlę',
    },
    '/panel/leady': {
      icon: MessageCircle, iconBg: 'bg-[#F47A3B]/10 text-[#F47A3B]',
      title: 'Zapytania',
      text: 'Tu widzisz kto napisał w sprawie szczeniąt. Odpowiadamy automatycznie. Ty finalizujesz.',
      tip: 'Kliknij zapytanie → odpowiedz i zamknij sprzedaż',
    },
    '/panel/wyniki': {
      icon: TrendingUp, iconBg: 'bg-emerald-50 text-emerald-600',
      title: 'Wyniki',
      text: 'Uczymy się, które posty o hodowli przyciągają najlepszych klientów.',
      tip: 'Sprawdzaj raz w tygodniu → widzisz co działa',
    },
    '/panel/psy': {
      icon: PawPrint, iconBg: 'bg-amber-50 text-amber-600',
      title: 'Twoje psy',
      text: 'Klienci chcą widzieć rodziców szczeniąt. Dodaj zdjęcia i rodowody.',
      tip: 'Kliknij „+ Dodaj psa" → zwiększ zaufanie klientów',
    },
    '/panel/subskrypcja': {
      icon: Zap, iconBg: 'bg-yellow-50 text-yellow-600',
      title: 'Plan',
      text: 'PRO = bez limitów. Więcej reklam = więcej zapytań o szczenięta.',
      tip: 'Plan PRO → publikuj bez limitu każdego dnia',
    },
  },
  biznes: {
    '/panel': {
      icon: Target, iconBg: 'bg-[#F47A3B]/10 text-[#F47A3B]',
      title: 'Twój panel',
      text: 'Tu widzisz ile osób widziało Twoją ofertę i ile napisało w sprawie usług.',
      tip: 'Kliknij „Posty na FB" → zaczniesz zbierać zapytania',
    },
    '/panel/kampania': {
      icon: Sparkles, iconBg: 'bg-[#F47A3B]/10 text-[#F47A3B]',
      title: 'Twoje reklamy',
      text: 'To są gotowe reklamy Twojej firmy. Kliknij „Publikuj" — zaczniesz zbierać zapytania.',
      tip: 'Publikuj 1–2 dziennie → więcej klientów na usługi',
    },
    '/panel/kreator-reklam': {
      icon: Image, iconBg: 'bg-purple-50 text-purple-600',
      title: 'Kreator reklam',
      text: 'W 10 sekund masz reklamę firmy gotową do publikacji.',
      tip: 'Wrzuć zdjęcie → stworzymy reklamę',
    },
    '/panel/publikacja': {
      icon: Send, iconBg: 'bg-emerald-50 text-emerald-600',
      title: 'Publikacja',
      text: 'Masz gotowy post do publikacji. Jeden klik i jest na Facebooku.',
      tip: 'Opublikuj teraz → zapytania zaczynają napływać',
    },
    '/panel/profil': {
      icon: Users, iconBg: 'bg-blue-50 text-blue-600',
      title: 'Profil firmy',
      text: 'Lepszy profil = więcej trafnych zapytań od klientów. Uzupełnij opis i ofertę.',
      tip: 'Uzupełnij wszystkie zakładki — klienci chcą widzieć ofertę',
    },
    '/panel/leady': {
      icon: MessageCircle, iconBg: 'bg-[#F47A3B]/10 text-[#F47A3B]',
      title: 'Zapytania',
      text: 'Tu widzisz kto napisał w sprawie oferty. System odpowiada automatycznie. Ty finalizujesz.',
      tip: 'Kliknij zapytanie → odpowiedz i zamknij sprzedaż',
    },
    '/panel/wyniki': {
      icon: TrendingUp, iconBg: 'bg-emerald-50 text-emerald-600',
      title: 'Wyniki',
      text: 'Uczymy się, które posty o Twojej firmie przyciągają najlepszych klientów.',
      tip: 'Sprawdzaj raz w tygodniu → widzisz co działa',
    },
    '/panel/psy': {
      icon: PawPrint, iconBg: 'bg-amber-50 text-amber-600',
      title: 'Zasoby',
      text: 'Dodaj zdjęcia i opisy — klienci chcą widzieć co oferujesz.',
      tip: 'Dodaj materiały → zwiększ zaufanie klientów',
    },
    '/panel/subskrypcja': {
      icon: Zap, iconBg: 'bg-yellow-50 text-yellow-600',
      title: 'Plan',
      text: 'PRO = bez limitów. Więcej reklam = więcej klientów.',
      tip: 'Plan PRO → publikuj bez limitu każdego dnia',
    },
  },
};

const DEFAULT_HINT = {
  icon: Lightbulb, iconBg: 'bg-zinc-50 text-zinc-500',
  title: 'Podpowiedź',
  text: 'Użyj menu po lewej, żeby przejść między sekcjami.',
  tip: 'Potrzebujesz pomocy? Napisz do nas.',
};

/* ═══ MICRO-STATUS ═══ */
function MicroStatus({ pathname }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    if (pathname.startsWith('/panel/kampania')) {
      axios.get(`${API}/fb-campaign/active`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          const posts = r.data?.posts || [];
          const pub = posts.filter(p => p.status === 'published').length;
          const ready = posts.filter(p => p.status === 'scheduled').length;
          setStats({ pub, ready, total: posts.length });
        }).catch(() => {});
    } else if (pathname.startsWith('/panel/kreator-reklam')) {
      axios.get(`${API}/ads/usage`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          const d = r.data;
          setStats({ used: d?.generations_used || 0, limit: d?.total_limit || 0 });
        }).catch(() => {});
    } else {
      setStats(null);
    }
  }, [pathname]);

  if (!stats) return null;

  if (pathname.startsWith('/panel/kampania') && stats.total) {
    return (
      <div className="bg-zinc-50 rounded-xl p-3 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-400">Opublikowanych</span>
          <span className="text-[11px] font-bold text-emerald-600">{stats.pub}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-400">Gotowych</span>
          <span className="text-[11px] font-bold text-[#F47A3B]">{stats.ready}</span>
        </div>
        <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden mt-1">
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(stats.pub / stats.total) * 100}%` }} />
        </div>
      </div>
    );
  }

  if (pathname.startsWith('/panel/kreator-reklam') && stats.limit) {
    return (
      <div className="bg-zinc-50 rounded-xl p-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-400">Użyto reklam</span>
          <span className="text-[11px] font-bold text-zinc-700">{stats.used} / {stats.limit}</span>
        </div>
      </div>
    );
  }

  return null;
}

/* ═══ POST-ACTION FLASH ═══ */
function useActionFlash() {
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      setFlash(e.detail);
      setTimeout(() => setFlash(null), 4000);
    };
    window.addEventListener('panel-action', handler);
    return () => window.removeEventListener('panel-action', handler);
  }, []);

  return flash;
}

/* ═══ MAIN COMPONENT ═══ */
export default function PanelContextHint() {
  const location = useLocation();
  const { user } = useAuth();
  const flash = useActionFlash();

  const segment = user?.profile_type === 'biznes' ? 'biznes' : 'hodowla';
  const copy = COPY[segment];

  const hint = useMemo(() => {
    const path = location.pathname;
    if (copy[path]) return copy[path];
    const match = Object.keys(copy)
      .filter(k => k !== '/panel' && path.startsWith(k))
      .sort((a, b) => b.length - a.length)[0];
    if (match) return copy[match];
    if (path.startsWith('/panel')) return copy['/panel'];
    return DEFAULT_HINT;
  }, [location.pathname, copy]);

  const Icon = hint.icon;

  return (
    <div className="hidden xl:flex w-48 shrink-0 border-l border-zinc-100 bg-white flex-col sticky top-0 h-screen" data-testid="panel-context-hint">
      <AnimatePresence mode="wait">
        <motion.div
          key={flash ? 'flash' : location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="px-3 py-3 flex flex-col gap-2"
        >
          {/* Flash state after action */}
          {flash ? (
            <>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600">
                <CheckCircle2 className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-[12px] font-bold text-emerald-700 mb-0.5">{flash.title}</h3>
                <p className="text-[10px] text-emerald-600 leading-snug">{flash.text}</p>
              </div>
            </>
          ) : (
            <>
              {/* Icon + Title inline */}
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${hint.iconBg}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <h3 className="text-[12px] font-bold text-zinc-900 leading-tight">{hint.title}</h3>
              </div>

              {/* Text */}
              <p className="text-[10px] text-zinc-500 leading-snug">{hint.text}</p>

              {/* Tip */}
              <div className="bg-[#F47A3B]/5 border border-[#F47A3B]/10 rounded-lg p-2">
                <div className="flex items-start gap-1.5">
                  <Zap className="w-2.5 h-2.5 text-[#F47A3B] mt-0.5 shrink-0" />
                  <p className="text-[9px] text-[#F47A3B] font-medium leading-snug">{hint.tip}</p>
                </div>
              </div>

              {/* Micro-status */}
              <MicroStatus pathname={location.pathname} />
            </>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Bottom: Primary CTA + WhatsApp */}
      <div className="px-3 py-3 mt-4 space-y-1.5 border-t border-zinc-100">
        <div
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-zinc-200 text-zinc-400 text-[10px] font-bold cursor-default"
          data-testid="hint-cta-primary"
        >
          <Sparkles className="w-3 h-3" /> Kreator grafik
          <span className="ml-1 text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-100 text-orange-500 font-bold">Wkrotce</span>
        </div>
        <a href="https://wa.me/48507334540?text=Hej%2C%20potrzebuje%20pomocy%20z%20ApisAI"
          target="_blank" rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-1.5 py-1 rounded-lg text-[8px] font-medium text-zinc-400 hover:text-[#F47A3B] transition-colors"
          data-testid="hint-whatsapp"
        >
          <MessageCircle className="w-2.5 h-2.5" /> Potrzebujesz pomocy?
        </a>
      </div>
    </div>
  );
}
